import { FC, HTMLAttributes, useCallback, useEffect, useState } from "react";
import { useStyles } from "./AchievementShareDialog.styles";
import { StaticImage } from "../StaticImage";
import { Typography } from "../../core/Typography/Typography";
import { Achievement } from "@onmo/graphql/graphql";
import { convertAchievement } from "../../../models/achievement";
import { Dialog } from "../../core/Dialog/Dialog";
import Avatar from "../../core/Avatar/Avatar";
import LogoIcon from "../../../assets/image/vodacom_logo_1x.webp";
import BackgroundImage from "./assets/vector.webp";
import { TypographyVariant } from "../../../constants/typography";
import { Icon } from "../../core/Icon/Icon";
import { useQuery } from "@apollo/client";
import { meGQL } from "@onmo/graphql/graphql";
import { useTranslation } from "react-i18next";
import Button from "../../core/Button/Button";
import { useCreateShareUrl } from "../../../hooks/resolvers/user/user";
import { NativeShare } from "../../../models/nativeShare";

interface IAchievementShareDialogProps {
  open: boolean;
  achievement: Achievement;
  onClose: () => void;
}

export const AchievementShareDialog: FC<
  IAchievementShareDialogProps & HTMLAttributes<HTMLDivElement>
> = ({ open, achievement, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { data: dataMe } = useQuery(meGQL);
  const { achievementName, achievementImage } = convertAchievement(achievement);
  const { createShareUrl } = useCreateShareUrl();
  const [shareUrl, setShareUrl] = useState<string>();
  const [isLoadedAvatar, setLoadedAvatar] = useState(false);

  const onShare = () => {
    NativeShare.share({ url: shareUrl });
  };

  const getShareUrl = useCallback(async () => {
    const shareUrl = await createShareUrl();
    setShareUrl(shareUrl);
  }, [createShareUrl]);

  useEffect(() => {
    if (open && isLoadedAvatar) {
      getShareUrl();
    }
  }, [getShareUrl, open, isLoadedAvatar]);

  return (
    <Dialog open={open} className={classes.shareDialog}>
      <Icon
        name="icon-bold-close"
        onClick={onClose}
        className={classes.closeIcon}
      />
      <div className={classes.root} id="shareContent">
        <StaticImage src={LogoIcon} className={classes.logo} />
        <div className={classes.content}>
          <StaticImage src={BackgroundImage} className={classes.background} />
          <div>
            <Typography
              variant={TypographyVariant.HeadingH4Bold}
              className={classes.achievementText}
            >
              {t("common_New Achievement")}
            </Typography>
            <Typography
              variant={TypographyVariant.BodyB5Medium}
              className={classes.achievementTitle}
            >
              {t(achievementName || "")}
            </Typography>
          </div>
          <StaticImage
            src={achievementImage?.src1x}
            src2x={achievementImage?.src2x}
            src3x={achievementImage?.src3x}
            className={classes.currentImage}
          />
        </div>
        <div className={classes.currentUser}>
          <Avatar
            url={dataMe?.me?.avatar}
            size="s"
            className={classes.avatar}
            onLoad={() => setLoadedAvatar(true)}
          />
          <Typography
            variant={TypographyVariant.BodyB4Regular}
            className={classes.userNameText}
          >
            {dataMe?.me?.username}
          </Typography>
        </div>
      </div>
      <Button
        variant="contained"
        className={classes.btnContent}
        onClick={onShare}
        afterIcon="icon-bold-share"
        fullWidth
        loading={!shareUrl}
      >
        <Typography
          variant={TypographyVariant.BodyB3Bold}
          className={classes.btnText}
        >
          {t("common_Share")}
        </Typography>
      </Button>
    </Dialog>
  );
};
